/**
 * Risk Management API Service
 * Handles all risk register, assessment, treatment, appetite, and KRI operations
 */

import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' }
});

api.interceptors.request.use((config) => {
  config.headers = { ...config.headers, ...getAuthHeaders() };
  return config;
});

// ==================== Risk Register ====================

export const getRiskRegister = async (params = {}) => {
  const response = await api.get('/api/risk/register', { params });
  return response.data;
};

export const getRiskById = async (riskId) => {
  const response = await api.get(`/api/risk/register/${riskId}`);
  return response.data;
};

export const createRisk = async (riskData) => {
  const response = await api.post('/api/risk/register', riskData);
  return response.data;
};

export const updateRisk = async (riskId, updateData) => {
  const response = await api.put(`/api/risk/register/${riskId}`, updateData);
  return response.data;
};

export const deleteRisk = async (riskId) => {
  const response = await api.delete(`/api/risk/register/${riskId}`);
  return response.data;
};

// ==================== Risk Assessments ====================

export const getRiskAssessments = async (params = {}) => {
  const response = await api.get('/api/risk/assessments', { params });
  return response.data;
};

export const getRiskAssessmentById = async (assessmentId) => {
  const response = await api.get(`/api/risk/assessments/${assessmentId}`);
  return response.data;
};

export const createRiskAssessment = async (assessmentData) => {
  const response = await api.post('/api/risk/assessments', assessmentData);
  return response.data;
};

export const updateRiskAssessment = async (assessmentId, updateData) => {
  const response = await api.put(`/api/risk/assessments/${assessmentId}`, updateData);
  return response.data;
};

// ==================== Risk Treatments ====================

export const getRiskTreatments = async (params = {}) => {
  const response = await api.get('/api/risk/treatments', { params });
  return response.data;
};

export const getRiskTreatmentById = async (treatmentId) => {
  const response = await api.get(`/api/risk/treatments/${treatmentId}`);
  return response.data;
};

export const createRiskTreatment = async (treatmentData) => {
  const response = await api.post('/api/risk/treatments', treatmentData);
  return response.data;
};

export const updateRiskTreatment = async (treatmentId, updateData) => {
  const response = await api.put(`/api/risk/treatments/${treatmentId}`, updateData);
  return response.data;
};

// ==================== Risk Appetite ====================

export const getRiskAppetite = async () => {
  const response = await api.get('/api/risk/appetite');
  return response.data;
};

export const setRiskAppetite = async (appetiteData) => {
  const response = await api.post('/api/risk/appetite', appetiteData);
  return response.data;
};

// ==================== Key Risk Indicators ====================

export const getKRIs = async (params = {}) => {
  const response = await api.get('/api/risk/kris', { params });
  return response.data;
};

export const getKRIById = async (kriId) => {
  const response = await api.get(`/api/risk/kris/${kriId}`);
  return response.data;
};

export const createKRI = async (kriData) => {
  const response = await api.post('/api/risk/kris', kriData);
  return response.data;
};

export const updateKRIValue = async (kriId, valueData) => {
  const response = await api.put(`/api/risk/kris/${kriId}/value`, valueData);
  return response.data;
};

// ==================== Dashboard & Heatmap ====================

export const getRiskDashboard = async () => {
  const response = await api.get('/api/risk/dashboard');
  return response.data;
};

export const getRiskHeatmap = async () => {
  const response = await api.get('/api/risk/heatmap');
  return response.data;
};

// ==================== Helper Functions ====================

export const RISK_CATEGORIES = [
  { value: 'operational', label: 'Operational', color: 'blue' },
  { value: 'financial', label: 'Financial', color: 'green' },
  { value: 'strategic', label: 'Strategic', color: 'purple' },
  { value: 'compliance', label: 'Compliance', color: 'orange' },
  { value: 'technology', label: 'Technology', color: 'cyan' },
  { value: 'reputational', label: 'Reputational', color: 'pink' },
  { value: 'cybersecurity', label: 'Cybersecurity', color: 'red' },
  { value: 'third_party', label: 'Third Party', color: 'yellow' },
  { value: 'regulatory', label: 'Regulatory', color: 'indigo' },
  { value: 'environmental', label: 'Environmental', color: 'teal' }
];

export const RISK_STATUSES = [
  { value: 'identified', label: 'Identified', color: 'gray' },
  { value: 'assessed', label: 'Assessed', color: 'blue' },
  { value: 'mitigating', label: 'Mitigating', color: 'yellow' },
  { value: 'accepted', label: 'Accepted', color: 'green' },
  { value: 'transferred', label: 'Transferred', color: 'purple' },
  { value: 'closed', label: 'Closed', color: 'gray' }
];

export const TREATMENT_TYPES = [
  { value: 'mitigate', label: 'Mitigate', icon: '🛡️' },
  { value: 'accept', label: 'Accept', icon: '✅' },
  { value: 'transfer', label: 'Transfer', icon: '↗️' },
  { value: 'avoid', label: 'Avoid', icon: '🚫' }
];

export const getRiskLevelFromScore = (score) => {
  if (score >= 20) return { level: 'critical', color: 'red', label: 'Critical' };
  if (score >= 12) return { level: 'high', color: 'orange', label: 'High' };
  if (score >= 6) return { level: 'medium', color: 'yellow', label: 'Medium' };
  return { level: 'low', color: 'green', label: 'Low' };
};

export const getCategoryColor = (category) => {
  const cat = RISK_CATEGORIES.find(c => c.value === category);
  return cat?.color || 'gray';
};

export const getStatusColor = (status) => {
  const stat = RISK_STATUSES.find(s => s.value === status);
  return stat?.color || 'gray';
};

export default {
  getRiskRegister,
  getRiskById,
  createRisk,
  updateRisk,
  deleteRisk,
  getRiskAssessments,
  getRiskAssessmentById,
  createRiskAssessment,
  updateRiskAssessment,
  getRiskTreatments,
  getRiskTreatmentById,
  createRiskTreatment,
  updateRiskTreatment,
  getRiskAppetite,
  setRiskAppetite,
  getKRIs,
  getKRIById,
  createKRI,
  updateKRIValue,
  getRiskDashboard,
  getRiskHeatmap
};
